/**
 * AuthContext - SSO Authentication state management
 * Integrates with IWA Apps SSO (apps.iwa.web.tr)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { logger } from '../utils/logger';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'https://apps.iwa.web.tr';
const APP_CODE = 'amzsellmetrics';

interface TokenPayload {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  apps: Record<string, string>; // { appCode: roleCode }
  iat: number;
  exp: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  role: string; // Role for this app (amzsellmetrics)
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  logout: () => void;
  isAdmin: boolean;
  isEditor: boolean;
  canEdit: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'sso_access_token';
const REFRESH_TOKEN_KEY = 'sso_refresh_token';
const USER_KEY = 'sso_user';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Decode JWT and extract user info
  const decodeToken = useCallback((token: string): User | null => {
    try {
      const payload = jwtDecode<TokenPayload>(token);
      logger.log('[AmzSell Auth] Token received, verifying...');

      // Check if token is expired
      if (payload.exp * 1000 < Date.now()) {
        logger.warn('[AmzSell Auth] Token expired');
        return null;
      }

      // Check if user has access to this app
      const role = payload.apps[APP_CODE];
      logger.log('[AmzSell Auth] Role:', role ? 'granted' : 'denied');
      if (!role) {
        logger.warn('[AmzSell Auth] No access to this application');
        return null;
      }

      logger.log('[AmzSell Auth] Authentication successful');
      return {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        role: role,
      };
    } catch (error) {
      logger.error('[AmzSell Auth] Token decode error:', error);
      return null;
    }
  }, []);

  // Initialize from localStorage or URL params (SSO callback)
  useEffect(() => {
    const initAuth = async () => {
      logger.log('[AmzSell Auth] Initializing authentication...');
      // Check if returning from SSO (tokens in URL hash or query params)
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      const accessToken = hashParams.get('token') || urlParams.get('token') || urlParams.get('accessToken') || hashParams.get('accessToken');
      const refreshToken = hashParams.get('refreshToken') || urlParams.get('refreshToken');

      logger.log('[AmzSell Auth] Checking URL tokens...');

      if (accessToken) {
        logger.log('[AmzSell Auth] Found access token in URL, storing...');
        // Callback from SSO - store tokens
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }

        const user = decodeToken(accessToken);
        if (user) {
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          setState({
            user,
            token: accessToken,
            isLoading: false,
            isAuthenticated: true,
          });

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        } else {
          logger.warn('[AmzSell Auth] Token decode failed for URL tokens');
        }
      }

      // Check existing tokens in localStorage
      const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      logger.log('[AmzSell Auth] Checking localStorage...');

      if (storedToken && storedUser) {
        const user = decodeToken(storedToken);

        if (user) {
          logger.log('[AmzSell Auth] Restored from localStorage');
          setState({
            user,
            token: storedToken,
            isLoading: false,
            isAuthenticated: true,
          });
          return;
        } else {
          logger.warn('[AmzSell Auth] Stored token invalid');
        }
      }

      // No valid authentication - redirect to SSO
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);

      setState({
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      });
    };

    initAuth();
  }, [decodeToken]);

  const logout = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    setState({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    });

    // Redirect to SSO logout
    window.location.href = `${SSO_URL}?logout=true`;
  }, []);

  const value: AuthContextType = {
    ...state,
    logout,
    isAdmin: state.user?.role === 'admin',
    isEditor: state.user?.role === 'editor',
    canEdit: state.user?.role === 'admin' || state.user?.role === 'editor',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
